import type {
  ProfitDateBasis,
  ReportDateBasis,
} from "@/modules/reports/domain/constants";
import {
  STOCK_MOVEMENT_REASONS,
  type StockMovementsQueryDTO,
} from "@/modules/reports/domain/contracts";
import {
  buildDateRangePreset,
  type ReportDatePreset,
  type ReportDateRangeState,
} from "@/modules/reports/domain/date-range-presets";

export const REPORTS_TABS = [
  "sales",
  "profit",
  "stock",
  "shipping",
  "customers",
  "custom_jobs",
] as const;
export type ReportsTab = (typeof REPORTS_TABS)[number];

export const SALES_SECTIONS = ["overview", "products"] as const;
export type SalesSection = (typeof SALES_SECTIONS)[number];

export const PRODUCT_SECTIONS = ["best", "worst", "frequency"] as const;
export type ProductSection = (typeof PRODUCT_SECTIONS)[number];

export const PROFIT_SECTIONS = ["overview", "marketing", "products"] as const;
export type ProfitSection = (typeof PROFIT_SECTIONS)[number];

export const STOCK_SECTIONS = [
  "valuation",
  "aging",
  "dead",
  "movements",
] as const;
export type StockSection = (typeof STOCK_SECTIONS)[number];

export const CUSTOMER_SECTIONS = [
  "overview",
  "top",
  "geo",
  "watch",
  "affinity",
  "cohorts",
] as const;
export type CustomerSection = (typeof CUSTOMER_SECTIONS)[number];

export const CUSTOMER_WATCHLIST_KINDS = [
  "cod_returns",
  "inactive",
  "high_new",
] as const;
export type CustomerWatchlistKind = (typeof CUSTOMER_WATCHLIST_KINDS)[number];

export const REPORT_DATE_PRESETS = ["today", "7d", "month", "custom"] as const;
export const REPORT_DATE_BASIS = ["created", "shipped"] as const;
export const PROFIT_DATE_BASIS = ["paid_at", "created_at"] as const;

export type ReportsSearch = {
  tab: ReportsTab;
  section: string;
  sub?: ProductSection;
  preset: ReportDatePreset;
  from?: string;
  to?: string;
  basis: ReportDateBasis;
  profitBasis: ProfitDateBasis;
  productId?: string;
  variantId?: string;
  agingMinDays: number;
  reason?: StockMovementsQueryDTO["reason"];
  platform?: string;
  campaign?: string;
  categoryId?: string;
  mappedProductId?: string;
  customerProvince?: string;
  customerDistrict?: string;
  watchKind?: CustomerWatchlistKind;
  minCodReturns?: number;
  cohortMonths?: number;
};

function isOneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
): value is T {
  return typeof value === "string" && allowed.includes(value as T);
}

function optionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function optionalInt(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

export function defaultSectionForTab(tab: ReportsTab): string {
  switch (tab) {
    case "profit":
    case "customers":
      return "overview";
    case "stock":
      return "valuation";
    default:
      return "overview";
  }
}

function normalizeSectionForTab(tab: ReportsTab, section: string): string {
  if (tab === "sales") {
    return isOneOf(section, SALES_SECTIONS) ? section : "overview";
  }
  if (tab === "profit") {
    return isOneOf(section, PROFIT_SECTIONS) ? section : "overview";
  }
  if (tab === "stock") {
    return isOneOf(section, STOCK_SECTIONS) ? section : "valuation";
  }
  if (tab === "shipping") {
    return "overview";
  }
  if (tab === "customers") {
    return isOneOf(section, CUSTOMER_SECTIONS) ? section : "overview";
  }
  if (tab === "custom_jobs") {
    return "overview";
  }
  return "overview";
}

export function validateReportsSearch(
  search: Record<string, unknown>,
): ReportsSearch {
  const tab = isOneOf(search.tab, REPORTS_TABS) ? search.tab : "sales";

  const rawSection =
    typeof search.section === "string"
      ? search.section
      : defaultSectionForTab(tab);
  const section = normalizeSectionForTab(tab, rawSection);

  const sub = isOneOf(search.sub, PRODUCT_SECTIONS) ? search.sub : undefined;
  const preset = isOneOf(search.preset, REPORT_DATE_PRESETS)
    ? search.preset
    : "7d";
  const basis = isOneOf(search.basis, REPORT_DATE_BASIS)
    ? search.basis
    : "created";
  const profitBasis = isOneOf(search.profitBasis, PROFIT_DATE_BASIS)
    ? search.profitBasis
    : "paid_at";

  const from = optionalString(search.from);
  const to = optionalString(search.to);
  const reason = isOneOf(search.reason, STOCK_MOVEMENT_REASONS)
    ? search.reason
    : undefined;

  return {
    tab,
    section,
    sub:
      tab === "sales" && section === "products" ? (sub ?? "best") : undefined,
    preset,
    from: preset === "custom" ? from : undefined,
    to: preset === "custom" ? to : undefined,
    basis,
    profitBasis,
    productId: optionalString(search.productId),
    variantId: optionalString(search.variantId),
    agingMinDays: optionalInt(search.agingMinDays, 30, 1, 3650),
    reason,
    platform: optionalString(search.platform),
    campaign: optionalString(search.campaign),
    categoryId: optionalString(search.categoryId),
    mappedProductId: optionalString(search.mappedProductId),
    customerProvince: optionalString(search.customerProvince),
    customerDistrict: optionalString(search.customerDistrict),
    watchKind: isOneOf(search.watchKind, CUSTOMER_WATCHLIST_KINDS)
      ? search.watchKind
      : "cod_returns",
    minCodReturns: optionalInt(search.minCodReturns, 1, 1, 20),
    cohortMonths: optionalInt(search.cohortMonths, 6, 3, 12),
  };
}

export function rangeFromReportsSearch(
  search: ReportsSearch,
): ReportDateRangeState {
  if (search.preset === "custom" && search.from && search.to) {
    return {
      from: search.from,
      to: search.to,
      basis: search.basis,
    };
  }
  const preset = search.preset === "custom" ? "7d" : search.preset;
  return buildDateRangePreset(preset, search.basis);
}

export function productFilterFromSearch(search: ReportsSearch) {
  return {
    productId: search.productId,
    variantId: search.variantId,
  };
}

export function salesSectionFromSearch(search: ReportsSearch): SalesSection {
  return search.section === "products" ? "products" : "overview";
}

export function profitSectionFromSearch(search: ReportsSearch): ProfitSection {
  if (search.section === "marketing") return "marketing";
  if (search.section === "products") return "products";
  return "overview";
}

export function stockSectionFromSearch(search: ReportsSearch): StockSection {
  if (search.section === "dead") return "dead";
  if (search.section === "movements") return "movements";
  if (search.section === "valuation") return "valuation";
  return "aging";
}

export function customerSectionFromSearch(
  search: ReportsSearch,
): CustomerSection {
  if (search.section === "top") return "top";
  if (search.section === "geo") return "geo";
  if (search.section === "watch") return "watch";
  if (search.section === "affinity") return "affinity";
  if (search.section === "cohorts") return "cohorts";
  return "overview";
}

export function reportsUsesDateRange(
  tab: ReportsTab,
  stockSection: StockSection,
  customerSection?: CustomerSection,
): boolean {
  if (tab === "customers" && customerSection === "cohorts") return false;
  return (
    tab === "sales" ||
    tab === "profit" ||
    tab === "shipping" ||
    tab === "customers" ||
    tab === "custom_jobs" ||
    (tab === "stock" && stockSection === "movements")
  );
}
